import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { SOCKET_ID_VERSION_CONFIG_KEY } from "../../utils/dynamicSockets/dynamicSocketId";

export const SANDBOX_LANGUAGES = [
  "typescript",
  "javascript",
  "python",
] as const;
export type SandboxLanguage = (typeof SANDBOX_LANGUAGES)[number];

export const DEFAULT_CODE: Record<SandboxLanguage, string> = {
  typescript: `function handler(input: Record<string, unknown>): Record<string, unknown> {
  return {
    result: input,
  };
}`,
  javascript: `function handler(input) {
  return {
    result: input,
  };
}`,
  python: `def handler(input):
    return {
        "result": input,
    }`,
};

export const SANDBOX_INPUT_KEY_TYPES = [
  "string",
  "number",
  "boolean",
  "json",
  "any",
  "array<string>",
  "array<number>",
  "array<boolean>",
  "array<json>",
] as const;
export type SandboxInputKeyType = (typeof SANDBOX_INPUT_KEY_TYPES)[number];

export const SANDBOX_OUTPUT_KEY_TYPES = [
  "string",
  "number",
  "boolean",
  "json",
  "any",
  "array<string>",
  "array<number>",
  "array<boolean>",
  "array<json>",
] as const;
export type SandboxOutputKeyType = (typeof SANDBOX_OUTPUT_KEY_TYPES)[number];

export const codeSandboxStep = defineStepConfig({
  type: "code_sandbox",
  name: "Code Sandbox",
  description: "Run custom code in an isolated Daytona sandbox",
  category: "Utility",

  inputSchema: z.record(z.string(), z.unknown()),

  outputSchema: z.record(z.string(), z.unknown()),

  configSchema: z.object({
    language: z.enum(SANDBOX_LANGUAGES).default("typescript"),
    code: z.string().max(100_000).default(DEFAULT_CODE.typescript),
    sandboxConfigId: z.string().uuid().optional(),
    inputKeys: z
      .array(
        z.object({
          key: z.string().regex(/^[a-z][a-z0-9_]*$/),
          type: z.enum(SANDBOX_INPUT_KEY_TYPES).default("any"),
          description: z.string().optional(),
          required: z.boolean().default(true),
        }),
      )
      .default([]),
    outputKeys: z
      .array(
        z.object({
          key: z.string().regex(/^[a-z][a-z0-9_]*$/),
          type: z.enum(SANDBOX_OUTPUT_KEY_TYPES).default("any"),
          description: z.string().optional(),
        }),
      )
      .default([{ key: "result", type: "any" }]),
    timeoutSeconds: z.number().min(1).max(60).default(10),
    // Declared so z.object() does not strip the stamp on parse. `executeStep`
    // parses the persisted config through this schema before handing it to the
    // handler, and the handler uses the version to decide whether to remap
    // socket ids to the declared keys. Drop the stamp and the remap is skipped:
    // the sandbox is handed `$key::dynamic` variables the author's code never
    // references, so every declared input reads undefined INSIDE the user's
    // code — no throw, no failed step, just wrong answers.
    [SOCKET_ID_VERSION_CONFIG_KEY]: z
      .number()
      .int()
      .optional()
      .describe(
        "Socket-id scheme this step was created under. Absent means the legacy scheme, where a declared key IS its socket id.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: { maximumAttempts: 1 },
  },
});
